














import Vue from 'vue'
import * as moment from 'moment-mini-ts'
import { orderBy, debounce } from 'lodash'

import MessagesList from './MessagesList'

import { Component, Watch, Prop } from 'vue-property-decorator'
import { BlipAccount, BlipMessage } from '@/models'
import { MessageCommandProcessor } from '@/api'
import { MessageReceiver, BrowserNotification } from '@/utils'
import { State, Getter, Mutation } from 'vuex-class'
import defaultBotImage from '@/assets/images/avatar-default.png'
import { AppSettings } from '@/appSettings'

@Component({
  components: { MessagesList },
})
export default class ThreadMessages extends Vue {
  @State client
  @Getter botIdentity
  @Mutation('setHasMessages') mutationHasMessages
  @Mutation('setApplicationLoading') mutationApplicationLoading

  @Prop({ type: Object, required: false })
  customSendMessage: object

  @Prop({ type: Boolean })
  isReadyToGet: boolean

  @Prop({ type: Boolean })
  disableHistory: boolean

  @Prop({ type: Object })
  botAccount: BlipAccount

  @Prop({ type: Object, required: true })
  translations: Object

  messages: BlipMessage[] = []
  hiddenMessages: BlipMessage[] = []

  @Watch('isReadyToGet')
  addClientListeners(val: boolean, oldVal: boolean) {
    if (val === true && oldVal === false) {
      const messageReceiver = new MessageReceiver()
      const notificationsApi = new BrowserNotification()
      // Receive bot messages
      this.client.instance.addMessageReceiver(
        messageReceiver.filter,
        (message) => {
          const processedMessage = messageReceiver.process(message)

          // If theres a message with the same id in the thread, ignore it.
          // It can be a retry message from the server.
          if (
            !processedMessage.id ||
            !this.messages.find((m) => m.id == processedMessage.id)
          ) {
            // Chatstate paused is only permitted if previous message is chatstate composing
            if (
              this.messages &&
              this.messages.length > 0 &&
              processedMessage.type === 'application/vnd.lime.chatstate+json' &&
              processedMessage.content.state === 'paused' &&
              this.messages[this.messages.length - 1].content.state !==
                'composing'
            ) {
              return
            }

            if (
              processedMessage.type === 'application/vnd.lime.input+json' &&
              processedMessage.content.validation &&
              processedMessage.content.validation.rule === 'type' &&
              processedMessage.content.validation.type === 'application/vnd.lime.sensitive+json'
            ) {
              this.$parent.$emit('setMessageType', processedMessage.content.validation.type)

              if (processedMessage.content.label) {
                processedMessage.type = processedMessage.content.label.type
                processedMessage.content = processedMessage.content.label.value
              }
            }

            if (
              processedMessage.type === 'application/vnd.lime.sensitive+json') {
                if (processedMessage.direction === 'sent') {
                  // Do not show if it was sent by the user
                  processedMessage.type = 'text/plain'
                  processedMessage.content = '**********'
                } else if (
                  processedMessage.content &&
                  processedMessage.content.type &&
                  processedMessage.content.value
                ) {
                  // Unwrap a received sensitive content
                  processedMessage.type = processedMessage.content.type
                  processedMessage.content = processedMessage.content.value
                }
            }

            this.messages = this.messages.concat(processedMessage)
            this.hiddenMessages = this.hiddenMessages.concat(processedMessage)
            //Move chat state composing to end if user sends a message
            if (
              this.messages &&
              this.messages.length > 1 &&
              this.messages[this.messages.length - 2].content.state ===
                'composing' &&
              this.messages[this.messages.length - 1].direction !== 'received'
            ) {
              this.messages = this.moveItem(
                this.messages,
                this.messages.length - 2,
                this.messages.length - 1
              )
            }

            notificationsApi.sendNotification(processedMessage)
            this.mutationHasMessages(true)
          }
        }
      )

      if (AppSettings.get('SHOW_MESSAGE_STATUS') === 'true') {
        this.client.instance.addNotificationReceiver(
          true,
          (notification) => {
            const message = this.messages.find((m) => m.id == notification.id)
            if (!message) {
              return
            }

            if (
              !message.status ||
              AppSettings.get('NOTIFICATION_EVENTS')[message.status] < AppSettings.get('NOTIFICATION_EVENTS')[notification.event]
            ) {
              this.$set(message, 'status', notification.event)
            }

            if (notification.event === 'failed') {
              console.error('Failed notification received', notification)
            }
          }
        )
      }
    }
  }

  async getThreadMessages($state?) {
    try {
      if (this.disableHistory) {
        $state.complete()

        return
      }

      const messageId =
        this.messages.length !== 0 ? this.messages[0].id : undefined

      const storageDate =
        this.messages.length !== 0 ? this.messages[0].date : undefined

      const {
        resource: { items },
      } = await MessageCommandProcessor.getMessages(
        this.botIdentity,
        messageId,
        storageDate
      )

      if (items.length === 0) {
        $state.complete()

        return
      }

      items.forEach((item) => {
        if (
          item.metadata['#blip.payload.content'] ||
          item.metadata['#blip.payload.text']
        ) {
          item.type = item.metadata['#blip.payload.type'] || 'text/plain'
          item.content =
            item.metadata['#blip.payload.content'] ||
            item.metadata['#blip.payload.text']

          if (item.type !== 'text/plain') {
            try {
              item.content = JSON.parse(item.content)
            } catch (e) {}
          }
        }
      })

      // Hide redirect messages from thread
      let msgList = items.filter((msg) => {
        if (msg.type === 'application/vnd.lime.redirect+json') {
          return false
        }
        if (msg.type === 'application/vnd.iris.ticket+json') {
          return false
        }
        return true
      })

      $state.loaded()

      this.messages = orderBy([...msgList, ...this.messages], ['date'], ['asc'])
      this.hiddenMessages = [...this.messages]

      this.mutationHasMessages(this.messages.length !== 0)
    } catch (e) {
      $state.complete()
    } finally {
      this.mutationApplicationLoading(false)
    }
  }

  get computedMessages() {
    const shouldShowPhoto = (a, i) => a[i].direction === 'received'

    if (AppSettings.get('REMOVE_DUPLICATED_MESSAGES') === 'true') {
      this.removeDuplicatedMessages()
    }

    this.messages = orderBy([...this.messages], ['date'], ['asc'])
    this.hideLastChatState()
    return (
      this.messages
        .map((m, i, a) => ({
          ...m,
          position: m.direction === 'received' ? 'left' : 'right',
          date: moment(m.date).format('HH:mm'),
          storageDate: m.date,
          createDate: moment(m.date).format(),
          photo: shouldShowPhoto(a, i)
            ? this.botAccount.photoUri
              ? this.botAccount.photoUri
              : defaultBotImage
            : '',
          hideOptions: i === a.length - 1 ? false : true,
          status: AppSettings.get('SHOW_MESSAGE_STATUS') === 'true' ? m.status : undefined,
        }))
        .filter(
          (m, i, a) =>
            !(
              this.isComposingChatstateAndNotLastMessage(m,i,a) ||
              this.isPausedChatstate(m) ||
              this.isHiddenMessage(m)
            )
        ) || []
    )
  }

  private isComposingChatstateAndNotLastMessage(m: any, i: number, a: any[]) {
    return m.type.includes('chatstate') &&
      m.content.state === 'composing' &&
      i != a.length - 1
  }

  private isPausedChatstate(m: any): boolean {
    return m.type.includes('chatstate') &&
      m.content.state === 'paused'
  }

  private isHiddenMessage(m: any): boolean {
    return m.metadata['#blip.hiddenMessage']
      && m.metadata['#blip.hiddenMessage'].toLowerCase() == 'true'
  }

  get computedHiddenMessages() {
    const shouldShowPhoto = (a, i) => a[i].direction === 'received'

    this.hiddenMessages = orderBy([...this.hiddenMessages], ['date'], ['asc'])

    return (
      this.hiddenMessages
        .map((m, i, a) => ({
          ...m,
          position: m.direction === 'received' ? 'left' : 'right',
          date: moment(m.date).format('HH:mm'),
          photo: shouldShowPhoto(a, i)
            ? this.botAccount.photoUri
              ? this.botAccount.photoUri
              : defaultBotImage
            : '',
          hideOptions: i === a.length - 1 ? false : true,
          status: AppSettings.get('SHOW_MESSAGE_STATUS') === 'true' ? m.status : undefined,
        }))
        .filter(
          (m, i, a) =>
            m.metadata['#blip.hiddenMessage'] &&
            m.metadata['#blip.hiddenMessage'].toLowerCase() == 'true' // message should be hidden
        ) || []
    )
  }

  hideLastChatState = debounce(
    function(this) {
      let lastMessage = this.messages[this.messages.length - 1]
      while (
        lastMessage &&
        lastMessage.type.includes('chatstate') &&
        lastMessage.content.state === 'composing'
      ) {
        this.messages.pop()
        lastMessage = this.messages[this.messages.length - 1]
      }
    },
    AppSettings.get('CHATSTATE_TIMEOUT'),
    { leading: false, trailing: true }
  )

  removeDuplicatedMessages() {
    const uniqueMessage: any[] = []
    this.messages.forEach(m => {
      if (m.type.includes('chatstate') || !uniqueMessage.find(msg => msg.id === m.id)) {
        uniqueMessage.push(m)
      }
    })
    this.messages = uniqueMessage
  }

  moveItem(array: any[], from: number, to: number): any[] {
    let removedItem = array.splice(from, 1)[0]
    array.splice(to, 0, removedItem)

    return array
  }
}



// WEBPACK FOOTER //
// ./node_modules/vue-loader/lib/selector.js?type=script&index=0!./src/components/ThreadMessages.vue